import { YStack } from '@/components/ui/layout/layout';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { Markdown, Smoothdown } from '@/components/ui/smoothdown';
import {
  type FrameStats,
  type TraversedConversationMessage,
} from '@/features/request-logger/api-logs.types';

export const TraversedContentViewer = ({ content }: { content: string }) => {
  return (
    <YStack>
      <h4 className="text-sm font-semibold mb-2 text-foreground-subtle">
        Traversed Content
      </h4>
      {content?.length > 0 ? (
        <div className="rounded-lg overflow-hidden">
          <ScrollArea className="h-[400px] space-y-4 border border-border rounded-md">
            <Markdown
              id="traversed-content-viewer-markdown"
              content={content}
            />
          </ScrollArea>
        </div>
      ) : (
        <div className="text-center p-8 text-foreground-subtle">
          No content available to display
        </div>
      )}
    </YStack>
  );
};

interface MetadataViewerProps {
  window: string;
  detectedApp: string;
  timestamp: string;
  type: string;
  contentType: string;
  url?: string;
}
export const MetadataViewer = ({
  window,
  detectedApp,
  timestamp,
  type,
  contentType,
  url,
}: MetadataViewerProps) => {
  return (
    <YStack>
      <h4 className="text-sm font-semibold mb-2 text-foreground-subtle">
        Meta Data
      </h4>
      <div className="bg-surface p-3 border border-border rounded-md">
        <h3 className="text-foreground font-medium">
          {window || 'Content Viewer'}
        </h3>
        <div className="flex flex-wrap gap-4 text-xs text-foreground-subtle mt-1">
          {detectedApp && <div>App: {detectedApp}</div>}
          {url && <div>URL: {url}</div>}
          {timestamp && <div>Time: {new Date(timestamp).toLocaleString()}</div>}
          {type && <div>Type: {type}</div>}
          {contentType && <div>Content Type: {contentType}</div>}
        </div>
      </div>
    </YStack>
  );
};

export const ConversationDataViewer = ({
  messages,
}: {
  messages: TraversedConversationMessage[];
}) => {
  return (
    <YStack>
      <h4 className="text-sm font-semibold mb-2 text-foreground-subtle">
        Conversation Data
      </h4>
      {messages?.length > 0 ? (
        <div className="bg-background border border-border rounded-md overflow-hidden">
          <ScrollArea className="h-[400px] border rounded-md space-y-4 bg-background border-border">
            <Markdown
              id="traversed-content-message"
              content={`\`\`\`json\n${JSON.stringify(messages, null, 2)}\n\`\`\``}
            />
          </ScrollArea>
        </div>
      ) : (
        <div className="text-center p-8 text-foreground-subtle">
          No content available to display
        </div>
      )}
    </YStack>
  );
};

export const DocumentDataViewer = ({
  documentData,
}: {
  documentData: string;
}) => {
  return (
    <YStack>
      <h4 className="text-sm font-semibold mb-2 text-foreground-subtle">
        Document Content
      </h4>
      {documentData?.length > 0 ? (
        <div className="rounded-lg overflow-hidden">
          <ScrollArea className="h-[400px] space-y-4 border border-border rounded-md">
            <Smoothdown id="document-data" content={documentData} />
          </ScrollArea>
        </div>
      ) : (
        <div className="text-center p-8 text-foreground-subtle">
          No content available to display
        </div>
      )}
    </YStack>
  );
};

export const FrameStatsViewer = ({ data }: { data: FrameStats }) => {
  if (!data) return null;

  return (
    <YStack>
      <h4 className="text-sm font-semibold mb-2 text-foreground-subtle">
        Frame Stats
      </h4>
      <YStack className="bg-surface border border-border rounded-md p-4 gap-4">
        <div className="grid grid-cols-4 gap-4">
          <YStack>
            <p className="text-xs text-foreground-subtle mb-1">
              avg sentence length
            </p>
            <p className="text-lg font-mono text-foreground">
              {data.avg_sentence_length?.toFixed(2) || 'N/A'}
            </p>
          </YStack>
          <YStack>
            <p className="text-xs text-foreground-subtle mb-1">
              capture duration
            </p>
            <p className="text-lg font-mono text-foreground">
              {data.capture_duration_ms !== undefined
                ? `${data.capture_duration_ms}ms`
                : 'N/A'}
            </p>
          </YStack>
          <YStack>
            <p className="text-xs text-foreground-subtle mb-1">
              compression ratio
            </p>
            <p className="text-lg font-mono text-foreground">
              {data.compression_ratio?.toFixed(4) || 'N/A'}
            </p>
          </YStack>
          <YStack>
            <p className="text-xs text-foreground-subtle mb-1">text size</p>
            <p className="text-lg font-mono text-foreground">
              {data.text_size !== undefined ? data.text_size : 'N/A'}
            </p>
          </YStack>
        </div>
        <div className="grid grid-cols-4 gap-4">
          <YStack>
            <p className="text-xs text-foreground-subtle mb-1">flesch score</p>
            <p className="text-lg font-mono text-foreground">
              {data.flesch_score !== undefined ? data.flesch_score : 'N/A'}
            </p>
          </YStack>
          <YStack>
            <p className="text-xs text-foreground-subtle mb-1">
              readability score
            </p>
            <p className="text-lg font-mono text-foreground">
              {data.readability_score !== undefined
                ? data.readability_score
                : 'N/A'}
            </p>
          </YStack>
          <YStack className="col-span-2">
            <p className="text-xs text-foreground-subtle mb-1">zlib quality</p>
            <p className="text-lg font-mono text-foreground">
              {data.zlib_quality !== undefined
                ? data.zlib_quality.toFixed(2)
                : 'N/A'}
            </p>
          </YStack>
        </div>
      </YStack>
    </YStack>
  );
};

interface ContentViewerProps {
  content: string;
  title: string;
}
export const ContentViewer = ({ content, title }: ContentViewerProps) => {
  return (
    <YStack>
      <h4 className="text-sm font-semibold mb-2 text-foreground-subtle">
        {title}
      </h4>
      {content?.length ? (
        <div className="mb-4">
          <div className="bg-background-subtle rounded-md p-4 overflow-hidden border border-border">
            <pre className="text-foreground text-xs overflow-x-auto scrollbar-thin">
              {content}
            </pre>
          </div>
        </div>
      ) : (
        <div className="text-center p-8 text-foreground-subtle">
          No content available to display
        </div>
      )}
    </YStack>
  );
};
